import { gql } from 'graphql-request';

// WHIT-482: the package-tour queries taurus-api serves (round 1). Each answers
// { status, error { code, message }, … } instead of throwing, so a page can
// tell "nothing found" from "the search failed".

export interface PackageMoney {
  amount: number;
  currency: string;
}

export interface PackageError {
  code: string | null;
  message: string | null;
}

interface Envelope {
  status: number;
  error: PackageError | null;
  // True when allmylespy's made-up stand-in answered (staging).
  standIn?: boolean | null;
}

export interface PackageDestinationNode {
  id: string;
  level: string;
  name: string;
  children?: PackageDestinationNode[] | null;
}

export interface PackageCode {
  code: string;
  name: string;
}

// WHIT-484: hotel only's reference data (hotelOnlySearchReferenceData) has
// no airports, departure places or transport types, and counts the stay in
// nights (nightsBounds) instead of days.
export interface PackageReferenceData extends Envelope {
  destinations: PackageDestinationNode[] | null;
  departureAirports?:
    { id: string; name: string; iata: string; countryCode: string }[] | null;
  boardTypes: (PackageCode & { rank: number })[] | null;
  roomTypes: PackageCode[] | null;
  amenities: PackageCode[] | null;
  transportTypes?: PackageCode[] | null;
  tourOperators: PackageCode[] | null;
  // Not from the API: the provider's English country name → the visitor's
  // language (utils/tourNames, WHIT-497).
  countryNames?: Record<string, string>;
  durationBounds?: {
    min: number;
    max: number;
    default: { from: number; to: number } | null;
  } | null;
  nightsBounds?: {
    min: number;
    max: number;
    default: { from: number; to: number } | null;
  } | null;
  occupancyBounds: {
    maxAdults: number;
    maxChildren: number;
    maxChildAge: number;
  } | null;
}

export interface PackageDestinationCard {
  id: string;
  level: string;
  name: string;
  parentId: string | null;
  leadInPrice: (PackageMoney & { basis: string }) | null;
  hotelCount: number | null;
  // WHIT-505: where its tag goes on the destinations map (APY-1522); null
  // when no position is known.
  geo?: PackageGeo | null;
}

export interface PackageDestinationOverview extends Envelope {
  destinations: PackageDestinationCard[] | null;
}

export interface PackageHotelResult {
  hotelId: string;
  name: string;
  location: { country: string; region: string; resort: string } | null;
  category: number | null;
  guestRatingPercent: number | null;
  distanceToBeachMeters: number | null;
  thumbnails: string[] | null;
  leadIn: {
    price: PackageMoney | null;
    boardType: string | null;
    departureAirportId: string | null;
    transportType: string | null;
    // WHIT-523: whose offer it is; null when the provider names none.
    tourOperator?: PackageCode | null;
  } | null;
  // WHIT-497: in the area only (accuracy UNKNOWN) — for the results map.
  geo?: PackageGeo | null;
}

export interface PackageFacetOption {
  id: string;
  name: string;
  count: number;
  children?: PackageFacetOption[] | null;
}

export interface PackageFacet {
  code: string;
  type: string;
  options: PackageFacetOption[] | null;
}

export interface PackageHotelSearch extends Envelope {
  results: PackageHotelResult[] | null;
  facets: PackageFacet[] | null;
  page: {
    number: number;
    size: number;
    totalResults: number;
    totalPages: number;
  } | null;
}

// The lists both search forms take; packages add airports, departure places
// and transport types, and count days where hotel only counts nights.
const REFERENCE_FIELDS = `
  status
  error {
    code
    message
  }
  destinations {
    id
    level
    name
    children {
      id
      level
      name
      children {
        id
        level
        name
      }
    }
  }
  boardTypes {
    code
    name
    rank
  }
  roomTypes {
    code
    name
  }
  amenities {
    code
    name
  }
  tourOperators {
    code
    name
  }
  occupancyBounds {
    maxAdults
    maxChildren
    maxChildAge
  }
`;

export const packageSearchReferenceDataQuery = gql`
  query packageSearchReferenceData($locale: String) {
    packageSearchReferenceData(locale: $locale) {
      ${REFERENCE_FIELDS}
      departureAirports {
        id
        name
        iata
        countryCode
      }
      transportTypes {
        code
        name
      }
      durationBounds {
        min
        max
        default {
          from
          to
        }
      }
    }
  }
`;

export const hotelOnlySearchReferenceDataQuery = gql`
  query hotelOnlySearchReferenceData($locale: String) {
    hotelOnlySearchReferenceData(locale: $locale) {
      ${REFERENCE_FIELDS}
      nightsBounds {
        min
        max
        default {
          from
          to
        }
      }
    }
  }
`;

export const packageDestinationOverviewQuery = gql`
  query packageDestinationOverview(
    $criteria: PackageSearchCriteriaInput!
    $locale: String
  ) {
    packageDestinationOverview(criteria: $criteria, locale: $locale) {
      standIn
      status
      error {
        code
        message
      }
      destinations {
        id
        level
        name
        parentId
        leadInPrice {
          amount
          currency
          basis
        }
        hotelCount
        geo {
          lat
          lon
        }
      }
    }
  }
`;

// The hotel list's fields; packages and hotel-only stays (WHIT-484) answer
// in the same shape, a stay's leadIn without airport or transport.
const HOTEL_SEARCH_FIELDS = `
  standIn
  status
  error {
    code
    message
  }
  results {
    hotelId
    name
    location {
      country
      region
      resort
    }
    category
    guestRatingPercent
    distanceToBeachMeters
    thumbnails
    leadIn {
      price {
        amount
        currency
      }
      boardType
      departureAirportId
      transportType
      tourOperator {
        code
        name
      }
    }
    geo {
      lat
      lon
      accuracy
    }
  }
  facets {
    code
    type
    options {
      id
      name
      count
      children {
        id
        name
        count
        children {
          id
          name
          count
        }
      }
    }
  }
  page {
    number
    size
    totalResults
    totalPages
  }
`;

export const packageHotelSearchQuery = gql`
  query packageHotelSearch(
    $criteria: PackageSearchCriteriaInput!
    $sort: PackageSortInput
    $page: PackagePageInput
    $locale: String
  ) {
    packageHotelSearch(
      criteria: $criteria
      sort: $sort
      page: $page
      locale: $locale
    ) {
      ${HOTEL_SEARCH_FIELDS}
    }
  }
`;

export const hotelOnlyHotelSearchQuery = gql`
  query hotelOnlyHotelSearch(
    $criteria: HotelOnlySearchCriteriaInput!
    $sort: PackageSortInput
    $page: PackagePageInput
    $locale: String
  ) {
    hotelOnlyHotelSearch(
      criteria: $criteria
      sort: $sort
      page: $page
      locale: $locale
    ) {
      ${HOTEL_SEARCH_FIELDS}
    }
  }
`;

// ---- round 3: the hotel page (P4) ------------------------------------------

export interface PackageGeo {
  lat: number | null;
  lon: number | null;
  // Only ADDRESS and STREET are exact enough for a pin or a distance.
  accuracy: string | null;
}

export interface PackageAddress {
  street: string | null;
  postCode: string | null;
  city: string | null;
  countryCode: string | null;
}

export interface PackageLookalike {
  hotelId: string;
  name: string;
  distanceMeters: number | null;
}

export interface PackageHotelDetail extends Envelope {
  hotelId: string;
  name: string;
  category: number | null;
  location: { country: string; region: string; resort: string } | null;
  address: PackageAddress | null;
  geo: PackageGeo | null;
  description: {
    sections: { title: string | null; text: string | null }[] | null;
    // GIATA | GENERATED (must be labelled) | NONE
    provenance: string | null;
    locale: string | null;
  } | null;
  images:
    | {
        thumbnail: string | null;
        medium: string | null;
        large: string | null;
      }[]
    | null;
  amenities: string[] | null;
  guestRatingPercent: number | null;
  distanceToBeachMeters: number | null;
  lookalikes: PackageLookalike[] | null;
}

export interface PackagePrice {
  perPerson: PackageMoney | null;
  total: PackageMoney | null;
}

// A package offer, or a hotel-only stay (WHIT-484): a stay has checkInDate
// and checkOutDate instead of departureDate, returnDate and days, and no
// transport.
export interface PackageOffer {
  offerId: string;
  token: string | null;
  departureDate?: string | null;
  returnDate?: string | null;
  checkInDate?: string | null;
  checkOutDate?: string | null;
  nights: number;
  days?: number | null;
  transport?: {
    type: string | null;
    departureAirportId: string | null;
    direct: boolean | null;
  } | null;
  room: {
    typeCode: string | null;
    view: string | null;
    operatorName: string | null;
  } | null;
  boardType: string | null;
  tourOperator: PackageCode | null;
  price: PackagePrice | null;
}

export interface PackageOfferFilterValue {
  id: string;
  name: string;
  count: number;
}

export interface PackageHotelOffers extends Envelope {
  hotelId: string;
  offers: PackageOffer[] | null;
  filters: {
    departureAirportIds?: PackageOfferFilterValue[] | null;
    boardTypes: PackageOfferFilterValue[] | null;
    roomTypes: PackageOfferFilterValue[] | null;
    roomViews: PackageOfferFilterValue[] | null;
    nights: PackageOfferFilterValue[] | null;
  } | null;
  page: {
    number: number;
    size: number;
    totalResults: number;
    totalPages: number;
  } | null;
}

// WHIT-495 (allmylespy APY-1517): set when online booking is not possible —
// the page offers the enquiry form instead. `reason` is for the agency only.
export interface PackageEnquirySignal {
  reason: string | null;
  enquiryId: string | null;
}

export interface PackageOfferVerification extends Envelope {
  offerId: string;
  // OK | ON_REQUEST | UNAVAILABLE
  availability: string | null;
  bookable: boolean | null;
  notBookableReason: string | null;
  price: PackagePrice | null;
  priceChanged: boolean | null;
  previousPrice: PackagePrice | null;
  // WHIT-483: what P5 books with. `token` may be newer than the list's;
  // `attemptId` (only when bookable) is the idempotency key of the booking.
  bookingModes?: string[] | null;
  token?: string | null;
  expiresAt?: string | null;
  attemptId?: string | null;
  optionExpiresAt?: string | null;
  alternativeRooms?: PackageAlternativeRoom[] | null;
  paymentSchedule?: PackagePaymentSchedule | null;
  // Also on a 502 answer, when online booking is not connected.
  enquiry?: PackageEnquirySignal | null;
}

export interface PackageAlternativeRoom {
  roomOptionId: string;
  roomTypeCode: string | null;
  operatorName: string | null;
  view: string | null;
  // Can be negative.
  surchargePerPerson: PackageMoney | null;
  availability: string | null;
}

export interface PackagePaymentSchedule {
  total: PackageMoney | null;
  firstPayment: PackageMoney | null;
  balanceDueDate: string | null;
  fullPaymentRequired: boolean | null;
}

// WHIT-503: hotels by name for the destination box (op 15). Only hotels a
// search of the same product offered lately, so the places stay listed too.
export interface PackageHotelName {
  hotelId: string;
  name: string | null;
  category: number | null;
  location: {
    country: string | null;
    countryCode: string | null;
    region: string | null;
    resort: string | null;
  } | null;
}

export interface PackageHotelNames extends Envelope {
  hotels: PackageHotelName[] | null;
}

const HOTEL_NAME_FIELDS = `
  status
  error {
    code
    message
  }
  standIn
  hotels {
    hotelId
    name
    category
    location {
      country
      countryCode
      region
      resort
    }
  }
`;

export const packageHotelNamesQuery = gql`
  query packageHotelNames($q: String!, $limit: Int, $locale: String) {
    packageHotelNames(q: $q, limit: $limit, locale: $locale) {
      ${HOTEL_NAME_FIELDS}
    }
  }
`;

export const hotelOnlyHotelNamesQuery = gql`
  query hotelOnlyHotelNames($q: String!, $limit: Int, $locale: String) {
    hotelOnlyHotelNames(q: $q, limit: $limit, locale: $locale) {
      ${HOTEL_NAME_FIELDS}
    }
  }
`;

export const packageHotelDetailQuery = gql`
  query packageHotelDetail($hotelId: String!, $locale: String) {
    packageHotelDetail(hotelId: $hotelId, locale: $locale) {
      status
      error {
        code
        message
      }
      hotelId
      name
      category
      location {
        country
        region
        resort
      }
      address {
        street
        postCode
        city
        countryCode
      }
      geo {
        lat
        lon
        accuracy
      }
      description {
        sections {
          title
          text
        }
        provenance
        locale
      }
      images {
        thumbnail
        medium
        large
      }
      amenities
      guestRatingPercent
      distanceToBeachMeters
      lookalikes {
        hotelId
        name
        distanceMeters
      }
    }
  }
`;

// An offer's fields beside its dates, and the list around the offers; a
// package offer adds departure, return, days and transport, a hotel-only stay
// check-in and check-out.
const OFFER_FIELDS = `
  offerId
  token
  nights
  room {
    typeCode
    view
    operatorName
  }
  boardType
  tourOperator {
    code
    name
  }
  price {
    perPerson {
      amount
      currency
    }
    total {
      amount
      currency
    }
  }
`;

const OFFER_LIST_FIELDS = `
  standIn
  status
  error {
    code
    message
  }
  hotelId
  page {
    number
    size
    totalResults
    totalPages
  }
`;

const OFFER_FILTER_FIELDS = `
  boardTypes {
    id
    name
    count
  }
  roomTypes {
    id
    name
    count
  }
  roomViews {
    id
    name
    count
  }
  nights {
    id
    name
    count
  }
`;

export const packageHotelOffersQuery = gql`
  query packageHotelOffers(
    $hotelId: String!
    $criteria: PackageSearchCriteriaInput!
    $offerFilters: PackageOfferFiltersInput
    $sort: PackageSortInput
    $page: PackagePageInput
    $locale: String
  ) {
    packageHotelOffers(
      hotelId: $hotelId
      criteria: $criteria
      offerFilters: $offerFilters
      sort: $sort
      page: $page
      locale: $locale
    ) {
      ${OFFER_LIST_FIELDS}
      offers {
        ${OFFER_FIELDS}
        departureDate
        returnDate
        days
        transport {
          type
          departureAirportId
          direct
        }
      }
      filters {
        ${OFFER_FILTER_FIELDS}
        departureAirportIds {
          id
          name
          count
        }
      }
    }
  }
`;

export const hotelOnlyHotelOffersQuery = gql`
  query hotelOnlyHotelOffers(
    $hotelId: String!
    $criteria: HotelOnlySearchCriteriaInput!
    $offerFilters: HotelOnlyOfferFiltersInput
    $sort: PackageSortInput
    $page: PackagePageInput
    $locale: String
  ) {
    hotelOnlyHotelOffers(
      hotelId: $hotelId
      criteria: $criteria
      offerFilters: $offerFilters
      sort: $sort
      page: $page
      locale: $locale
    ) {
      ${OFFER_LIST_FIELDS}
      offers {
        ${OFFER_FIELDS}
        checkInDate
        checkOutDate
      }
      filters {
        ${OFFER_FILTER_FIELDS}
      }
    }
  }
`;

const PAYMENT_SCHEDULE_FIELDS = gql`
  fragment PackagePaymentScheduleFields on PackagePaymentSchedule {
    total {
      amount
      currency
    }
    firstPayment {
      amount
      currency
    }
    balanceDueDate
    fullPaymentRequired
  }
`;

// WHIT-492: the tour operator's own information form (APY-1515, op 14).
export interface PackageOfferLegalForm {
  status: number;
  error: PackageError | null;
  offerId: string | null;
  // False: the operator has no form of its own; form is null.
  available: boolean | null;
  form: {
    // URL | HTML | PDF
    format: string | null;
    url: string | null;
    // The operator's HTML markup, or the PDF as base64.
    content: string | null;
    language: string | null;
    organiser: { code: string | null; name: string | null } | null;
  } | null;
  standIn: boolean | null;
}

export const packageOfferLegalFormQuery = gql`
  query packageOfferLegalForm($offerId: String!, $locale: String) {
    packageOfferLegalForm(offerId: $offerId, locale: $locale) {
      status
      error {
        code
        message
      }
      offerId
      available
      form {
        format
        url
        content
        language
        organiser {
          code
          name
        }
      }
      standIn
    }
  }
`;

export const packageOfferVerifyQuery = gql`
  query packageOfferVerify(
    $offerId: String!
    $token: String
    $occupancy: PackageOccupancyInput
    $locale: String
  ) {
    packageOfferVerify(
      offerId: $offerId
      token: $token
      occupancy: $occupancy
      locale: $locale
    ) {
      standIn
      status
      error {
        code
        message
      }
      offerId
      availability
      bookable
      notBookableReason
      price {
        perPerson {
          amount
          currency
        }
        total {
          amount
          currency
        }
      }
      priceChanged
      previousPrice {
        perPerson {
          amount
          currency
        }
        total {
          amount
          currency
        }
      }
      bookingModes
      token
      expiresAt
      attemptId
      optionExpiresAt
      alternativeRooms {
        roomOptionId
        roomTypeCode
        operatorName
        view
        surchargePerPerson {
          amount
          currency
        }
        availability
      }
      paymentSchedule {
        ...PackagePaymentScheduleFields
      }
      enquiry {
        reason
        enquiryId
      }
    }
  }
  ${PAYMENT_SCHEDULE_FIELDS}
`;

// ---- WHIT-483: booking (operations 7–10) ------------------------------------
// Queries, not mutations, as for flights. Each needs the customer's auth
// headers; an anonymous Firebase user counts. `error` is a refusal (nothing
// happened); a booking that failed at the tour operator is a `booking` with
// status FAILED and its own `error`.

export interface PackageFieldError {
  // A path into the create request, e.g. participants[1].birthDate.
  path: string | null;
  code: string | null;
  message: string | null;
}

export interface PackageBookingFailure {
  code: string | null;
  message: string | null;
  retryable: boolean | null;
  price: PackageMoney | null;
  previousPrice: PackageMoney | null;
  fieldErrors: PackageFieldError[] | null;
}

export interface PackageBooking {
  bookingId: string | null;
  attemptId: string | null;
  reference: string | null;
  // DRAFT | CONFIRMED | OPTION | ON_REQUEST | DECLINED | EXPIRED |
  // IN_FLIGHT | FAILED
  status: string;
  bookingMode: string | null;
  price: PackageMoney | null;
  paymentSchedule: PackagePaymentSchedule | null;
  paid: PackageMoney | null;
  remaining: PackageMoney | null;
  amountDue: PackageMoney | null;
  payBy: string | null;
  // NONE | PAYMENT_REQUIRED | CONFIRM | AWAIT_OPERATOR | AWAIT_RECONCILIATION
  nextAction: string | null;
  error: PackageBookingFailure | null;
  // A failure the customer cannot fix, or a declined request (WHIT-495).
  enquiry?: PackageEnquirySignal | null;
  standIn: boolean | null;
  hotel: {
    hotelId: string | null;
    name: string | null;
    city: string | null;
    geo: {
      lat: number | null;
      lon: number | null;
      accuracy: string | null;
    } | null;
  } | null;
  trip: {
    product: string | null;
    departureDate: string | null;
    returnDate: string | null;
    checkInDate: string | null;
    checkOutDate: string | null;
    days: number | null;
    nights: number | null;
    transport: {
      type: string | null;
      departureAirportId: string | null;
      direct: boolean | null;
    } | null;
    room: {
      typeCode: string | null;
      view: string | null;
      operatorName: string | null;
    } | null;
    boardType: string | null;
    tourOperator: PackageCode | null;
  } | null;
  travellers:
    | {
        type: string | null;
        title: string | null;
        firstName: string | null;
        lastName: string | null;
        birthDate: string | null;
      }[]
    | null;
  payments:
    | {
        paymentId: string | null;
        provider: string | null;
        amount: PackageMoney | null;
        recordedAt: string | null;
      }[]
    | null;
  refundsDue:
    | {
        paymentId: string | null;
        amount: PackageMoney | null;
        reason: string | null;
      }[]
    | null;
  laterPayment: {
    open: boolean | null;
    minimum: PackageMoney | null;
    maximum: PackageMoney | null;
    dueDate: string | null;
  } | null;
}

export interface PackageBookingResult {
  status: number;
  error: PackageError | null;
  booking: PackageBooking | null;
}

export interface PackagePaymentOrderResult extends PackageBookingResult {
  paymentOrder: {
    id: string | null;
    // Revolut's checkout token, or Stripe's client secret (staging only).
    token: string | null;
    client_secret: string | null;
    status: string | null;
  } | null;
  amount: PackageMoney | null;
}

const BOOKING_FIELDS = gql`
  fragment PackageBookingFields on PackageBooking {
    bookingId
    attemptId
    reference
    status
    bookingMode
    price {
      amount
      currency
    }
    paymentSchedule {
      ...PackagePaymentScheduleFields
    }
    paid {
      amount
      currency
    }
    remaining {
      amount
      currency
    }
    amountDue {
      amount
      currency
    }
    payBy
    nextAction
    error {
      code
      message
      retryable
      price {
        amount
        currency
      }
      previousPrice {
        amount
        currency
      }
      fieldErrors {
        path
        code
        message
      }
    }
    enquiry {
      reason
      enquiryId
    }
    standIn
    hotel {
      hotelId
      name
      city
      geo {
        lat
        lon
        accuracy
      }
    }
    trip {
      product
      departureDate
      returnDate
      checkInDate
      checkOutDate
      days
      nights
      transport {
        type
        departureAirportId
        direct
      }
      room {
        typeCode
        view
        operatorName
      }
      boardType
      tourOperator {
        code
        name
      }
    }
    travellers {
      type
      title
      firstName
      lastName
      birthDate
    }
    payments {
      paymentId
      provider
      amount {
        amount
        currency
      }
      recordedAt
    }
    refundsDue {
      paymentId
      amount {
        amount
        currency
      }
      reason
    }
    laterPayment {
      open
      minimum {
        amount
        currency
      }
      maximum {
        amount
        currency
      }
      dueDate
    }
  }
  ${PAYMENT_SCHEDULE_FIELDS}
`;

const RESULT_FIELDS = `
  status
  error {
    code
    message
  }
  booking {
    ...PackageBookingFields
  }
`;

export const packageBookingCreateQuery = gql`
  query packageBookingCreate($input: PackageBookingCreateInput!) {
    packageBookingCreate(input: $input) {
      ${RESULT_FIELDS}
    }
  }
  ${BOOKING_FIELDS}
`;

export const packagePaymentOrderCreationQuery = gql`
  query packagePaymentOrderCreation(
    $bookingId: String!
    $paymentProvider: String!
    $amount: PackageMoneyInput
    $email: String
  ) {
    packagePaymentOrderCreation(
      bookingId: $bookingId
      paymentProvider: $paymentProvider
      amount: $amount
      email: $email
    ) {
      ${RESULT_FIELDS}
      paymentOrder {
        id
        token
        client_secret
        status
      }
      amount {
        amount
        currency
      }
    }
  }
  ${BOOKING_FIELDS}
`;

export const packageBookingConfirmQuery = gql`
  query packageBookingConfirm(
    $attemptId: String!
    $bookingId: String!
    $paymentOrderId: String
  ) {
    packageBookingConfirm(
      attemptId: $attemptId
      bookingId: $bookingId
      paymentOrderId: $paymentOrderId
    ) {
      ${RESULT_FIELDS}
    }
  }
  ${BOOKING_FIELDS}
`;

export const packageBookingPaymentRecordQuery = gql`
  query packageBookingPaymentRecord(
    $bookingId: String!
    $paymentOrderId: String!
  ) {
    packageBookingPaymentRecord(
      bookingId: $bookingId
      paymentOrderId: $paymentOrderId
    ) {
      ${RESULT_FIELDS}
    }
  }
  ${BOOKING_FIELDS}
`;

export const packageBookingQuery = gql`
  query packageBooking($bookingId: String, $attemptId: String) {
    packageBooking(bookingId: $bookingId, attemptId: $attemptId) {
      ${RESULT_FIELDS}
    }
  }
  ${BOOKING_FIELDS}
`;

// WHIT-483 round 3: "My bookings" — the signed-in customer's package
// bookings, newest first.
export interface PackageBookingListResult {
  status: number;
  error: PackageError | null;
  bookings: PackageBooking[] | null;
}

export const packageBookingsQuery = gql`
  query packageBookings {
    packageBookings {
      status
      error {
        code
        message
      }
      bookings {
        ...PackageBookingFields
      }
    }
  }
  ${BOOKING_FIELDS}
`;

// "My bookings" opened from a link to one booking: the confirmation page's
// (its bookingId) or the email's (the order's urlId, from any browser).
export const packageBookingsClaimQuery = gql`
  query packageBookingsClaim($key: String!) {
    packageBookingsClaim(key: $key) {
      status
      message
      moved
    }
  }
`;

// After signing in where the customer booked anonymously: their package
// bookings follow them to the account.
export const packageBookingsMoveQuery = gql`
  query packageBookingsMove($anonymousToken: String!) {
    packageBookingsMove(anonymousToken: $anonymousToken) {
      status
      message
      moved
    }
  }
`;

// WHIT-485 round 3: the customer's favourite hotels (/kedvelt-szallasok/).
export interface PackageFavouriteHotel {
  hotelId: string;
  name: string | null;
}

export interface PackageFavouriteHotelsResult {
  status: number | null;
  // INVALID_HOTEL | TOO_MANY
  code: string | null;
  hotels: PackageFavouriteHotel[] | null;
}

const FAVOURITE_FIELDS = `
  status
  code
  hotels {
    hotelId
    name
  }
`;

export const packageFavouriteHotelsQuery = gql`
  query packageFavouriteHotels {
    packageFavouriteHotels {
      ${FAVOURITE_FIELDS}
    }
  }
`;

export const packageFavouriteHotelSetQuery = gql`
  query packageFavouriteHotelSet(
    $hotelId: String!
    $name: String
    $favourite: Boolean!
  ) {
    packageFavouriteHotelSet(
      hotelId: $hotelId
      name: $name
      favourite: $favourite
    ) {
      ${FAVOURITE_FIELDS}
    }
  }
`;

// ---- WHIT-495: an enquiry instead of a booking ------------------------------
// Exactly what the customer typed; allmylespy already has the trip under the
// enquiryId (booking not possible) or the offerId (only asking).

export interface PackageEnquiryInput {
  enquiryId?: string | null;
  offerId?: string | null;
  traveller: { name: string; email: string; phone?: string | null };
  message?: string | null;
  party?: { adults: number; childAges: number[] } | null;
  // EMAIL | PHONE
  preferredContact?: string | null;
  locale?: string | null;
  // The hidden field; only a bot fills it.
  website?: string | null;
}

export interface PackageEnquiryResult {
  status: number;
  // EXPIRED | IN_PROGRESS | NOT_CONFIGURED | TOO_MANY_REQUESTS |
  // INVALID_REQUEST | UNAVAILABLE
  error: PackageError | null;
  enquiryReference: string | null;
  receiptSent: boolean | null;
}

export const packageEnquirySendQuery = gql`
  query packageEnquirySend($input: PackageEnquiryInput!) {
    packageEnquirySend(input: $input) {
      status
      error {
        code
        message
      }
      enquiryReference
      receiptSent
    }
  }
`;
